import React, { useState } from 'react'
import { Modal } from 'react-bootstrap'
import { Formik, Form } from 'formik'
import { useParams } from 'react-router'
import {
  useErrorService,
  useNotificationService,
  Button,
} from '@abroad/components'
import { ExperienceSelect } from '../form'
import CurrencyInput from '../enterpriseSOW/CurrencyInput'
import API from '../../utils/API'
import * as Yup from 'yup'

const PAYMENT_METHOD_OPTIONS = [
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'wire_transfer', label: 'ACH' },
]

const ManualPaymentModal = ({
  showModal,
  handleClose,
  participant,
  updateParticipants,
  readOnly = false,
  paymentData = null,
}) => {
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const Error = useErrorService()
  const { questId } = useParams()
  const Notification = useNotificationService()

  const validationSchema = Yup.object().shape({
    amount: Yup.string()
      .required('Amount is required.')
      .test('amount-gt-zero', 'Amount must be greater than 0.', (value) => {
        if (!value) return true
        return parseFloat(value.replace(/[^0-9.]/g, '')) > 0
      }),
    paymentMethod: Yup.string().required('Payment method is required.'),
    reference: Yup.string(),
    internalNote: Yup.string(),
  })

  const getInitialValues = () => {
    if (readOnly && paymentData) {
      return {
        amount:
          paymentData.amount !== undefined && paymentData.amount !== null
            ? String(paymentData.amount)
            : '',
        paymentMethod: paymentData.paymentMethod || '',
        reference:
          paymentData.externalPaymentReference || paymentData.reference || '',
        internalNote: paymentData.adminNotes || paymentData.internalNote || '',
      }
    }

    return {
      amount: '',
      paymentMethod: '',
      reference: '',
      internalNote: '',
    }
  }

  const handleSubmit = async (values) => {
    if (readOnly) return

    setIsFormSubmitting(true)
    try {
      const payload = {
        paymentType: 'partial',
        customAmount: parseFloat(values.amount.replace(/[^0-9.]/g, '')),
        paymentMethod: values.paymentMethod,
        externalPaymentReference: values.reference || '',
        adminNotes: values.internalNote || '',
      }
      await API.AdminAPI.quests.addManualPayment(
        questId,
        participant.userId._id,
        payload,
      )
      Notification.showNotification(
        'Manual payment has been successfully',
        'success',
      )
      setIsFormSubmitting(false)
      handleClose()
      updateParticipants()
    } catch (e) {
      Error.showError(e)
      setIsFormSubmitting(false)
    }
  }

  if (readOnly) {
    if (!paymentData) return null
  } else if (!participant) {
    return null
  }

  const participantName =
    paymentData?.fullName ||
    participant?.userId?.fullName ||
    [participant?.userId?.fname, participant?.userId?.lname]
      .filter(Boolean)
      .join(' ') ||
    paymentData?.email ||
    participant?.userId?.email ||
    'participant'

  const readOnlyLead = [paymentData?.fullName, paymentData?.email]
    .filter(Boolean)
    .join(' · ')

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      dialogClassName='experience-add-part-dialog'
      contentClassName='experience-add-part-card'>
      <Modal.Body className='p-0'>
        <div className='experience-add-part'>
          <h2>Manual payment</h2>
          <p className='experience-add-part-lead'>
            {readOnly
              ? readOnlyLead || `Payment details for ${participantName}.`
              : `Record a payment for ${participantName}.`}
          </p>
          <Formik
            initialValues={getInitialValues()}
            enableReinitialize
            validationSchema={readOnly ? undefined : validationSchema}
            onSubmit={handleSubmit}>
            {({
              handleSubmit,
              errors,
              touched,
              values,
              handleBlur,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className='experience-add-part-fields'>
                  <div className='experience-f'>
                    <label className='experience-f-label'>Amount</label>
                    <CurrencyInput
                      inputClasses='form-control experience-f-control'
                      name='amount'
                      disabled={readOnly}
                    />
                    {!readOnly && errors.amount && touched.amount ? (
                      <p className='experience-add-part-error'>
                        {errors.amount}
                      </p>
                    ) : null}
                  </div>

                  <ExperienceSelect
                    name='paymentMethod'
                    label='Payment method'
                    options={PAYMENT_METHOD_OPTIONS}
                    placeholder='Select payment method'
                    isSearchable={false}
                    disabled={readOnly}
                    onChange={(e) =>
                      setFieldValue('paymentMethod', e.target.value)
                    }
                    onBlur={handleBlur}
                  />

                  <div className='experience-f'>
                    <label className='experience-f-label'>Reference</label>
                    {readOnly ? (
                      <div className='experience-f-value'>
                        {values.reference || '—'}
                      </div>
                    ) : (
                      <textarea
                        className='form-control experience-f-control experience-f-textarea'
                        name='reference'
                        rows={3}
                        value={values.reference}
                        onChange={(e) =>
                          setFieldValue('reference', e.target.value)
                        }
                        onBlur={handleBlur}
                        autoComplete='off'
                      />
                    )}
                  </div>

                  <div className='experience-f'>
                    <label className='experience-f-label'>Internal note</label>
                    {readOnly ? (
                      <div className='experience-f-value'>
                        {values.internalNote || '—'}
                      </div>
                    ) : (
                      <textarea
                        className='form-control experience-f-control experience-f-textarea'
                        name='internalNote'
                        rows={4}
                        value={values.internalNote}
                        onChange={(e) =>
                          setFieldValue('internalNote', e.target.value)
                        }
                        onBlur={handleBlur}
                        autoComplete='off'
                      />
                    )}
                  </div>
                </div>

                <div className='experience-modal-actions'>
                  <button
                    type='button'
                    className='btn-outline-experience'
                    onClick={handleClose}
                    disabled={isFormSubmitting}>
                    {readOnly ? 'Close' : 'Cancel'}
                  </button>
                  {!readOnly ? (
                    <Button
                      type='submit'
                      variant='saffron'
                      size='sm'
                      disabled={isFormSubmitting}
                      isLoading={isFormSubmitting}
                      className='btn-navy border-0'>
                      <span>Save</span>
                    </Button>
                  ) : null}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default ManualPaymentModal
